import { FC, useEffect, useState } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";

import {
  HStack,
  Progress,
  ProgressFilledTrack,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";

import { CountryProgressItem } from "./CountriesProgress";

const toFlag = (code: string) =>
  [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");

type BarRowProps = {
  delay: number;
  label: string;
  pct: number;
};

const BarRow: FC<BarRowProps> = ({ delay, label, pct }) => {
  const { i18n } = useLocalization();
  const [value, setValue] = useState(0);
  const opacity = useSharedValue(0);

  useEffect(() => {
    opacity.value = withDelay(delay, withTiming(1, { duration: 350 }));
    const t = setTimeout(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => setValue(pct)));
    }, delay + 300);
    return () => clearTimeout(t);
  }, [delay, opacity, pct]);

  const rowStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));

  return (
    <Animated.View style={rowStyle}>
      <HStack className="items-center gap-3">
        <Text
          className="text-xs font-medium text-typography-600 text-right"
          numberOfLines={1}
          style={{ flexShrink: 0, width: 112 }}
        >
          {label}
        </Text>
        <Progress className="flex-1 bg-background-100" size="sm" value={value}>
          <ProgressFilledTrack className="bg-primary-500 [transition:width_0.6s_ease-out]" />
        </Progress>
        <Text
          className="text-xs font-semibold text-typography-500"
          style={{ flexShrink: 0, width: 32 }}
        >
          {i18n.numberToPercentage(pct, { precision: 0 })}
        </Text>
      </HStack>
    </Animated.View>
  );
};

interface ResultsProcessingProps {
  countries: CountryProgressItem[];
}

const ResultsProcessing: FC<ResultsProcessingProps> = ({ countries }) => (
  <VStack className="gap-5 pt-8">
    {countries.slice(0, 12).map((item, i) => (
      <BarRow
        key={item.country}
        delay={i * 120}
        label={`${toFlag(item.country)} ${item.label}`}
        pct={
          item.total > 0 ? Math.round((item.completed / item.total) * 100) : 0
        }
      />
    ))}
  </VStack>
);

export default ResultsProcessing;
